import { Partner } from '../../types';

/**
 * Who a dispatch notification goes to. Each role gets its own message template
 * because they need different information: the driver needs from → to → SP3,
 * the drop-off facility needs an arrival notice, the crematorium needs the
 * intake letter with the signed agreement.
 *
 * Ontario is the exception: MacKinnon (transport) and the drop-off facility
 * take the SAME dispatch email (deceased + DOB + place of death + next of kin
 * + drop-off), and there is no SP3 equivalent in Ontario, so ON templates
 * simply omit it.
 */
export type DispatchRole = 'transport' | 'dropoff' | 'crematorium';

/** Case facts shared by every template. Destination = effective drop-off
 *  (drop-off partner if one is set, otherwise the crematorium). */
export interface DispatchFacts {
  funeralId: string | number;
  region: string;
  deceasedFirstName: string;
  deceasedLastName: string;
  deceasedDob: string;
  deceasedWeight: string;
  sp3Number: string;
  /** Facility name + address of where the deceased currently is. */
  placeOfDeath: string;
  transportName: string;
  destinationName: string;
  destinationAddress: string;
  crematoriumName: string;
  nextOfKinName: string;
  nextOfKinPhone: string;
  nextOfKinAddress: string;
}

export const formatPartnerAddress = (p?: Partner | null): string =>
  p ? [p.address, p.city, p.province, p.postal_code].filter(Boolean).join(', ') : '';

const fullName = (f: DispatchFacts) =>
  `${f.deceasedFirstName || ''} ${f.deceasedLastName || ''}`.trim();

const isOntario = (f: DispatchFacts) => f.region === 'ontario';

/** Asks the transport company to confirm they've taken the call. */
export const TRANSPORT_CONFIRM_LINE = 'SVP Confirmer Pour:';

/** Terse dispatch text for the driver — same shape staff has always sent.
 *  Ontario has no SP3, so that line is omitted there. The Text button offers
 *  this to any partner with a phone; only the transport company gets the
 *  confirmation request on top. */
export const buildTransportSms = (f: DispatchFacts, role: DispatchRole = 'transport'): string => {
  const destination = [f.destinationName, f.destinationAddress].filter(Boolean).join(', ');
  const lines = [
    ...(role === 'transport' ? [TRANSPORT_CONFIRM_LINE] : []),
    `${(f.deceasedLastName || '').toUpperCase()}, ${f.deceasedFirstName || ''}`,
    ...(isOntario(f) ? [] : [`SP3: ${f.sp3Number}`]),
    `From: ${f.placeOfDeath}`,
    `To: ${destination}`,
  ];
  return lines.join('\n');
};

export const buildEmailSubject = (role: DispatchRole, f: DispatchFacts): string => {
  const base = `${fullName(f)} (${f.funeralId})`;
  if (role === 'crematorium') return `Cleo Cremation - ${base}`;
  if (role === 'dropoff') return `Cleo Cremation - Incoming Transfer - ${base}`;
  return isOntario(f)
    ? `Cleo Cremation - Transfer Request - ${base}`
    : `Cleo Cremation - Transport Request - ${base}`;
};

/** The <h2> the backend renders at the top of the HTML email. */
export const emailHeading = (role: DispatchRole, f: DispatchFacts): string => {
  if (role === 'crematorium') return 'Cremation Request';
  if (role === 'dropoff') return 'Incoming Transfer';
  return isOntario(f) ? 'Transfer Request' : 'Transport Request';
};

export const buildEmailBody = (
  role: DispatchRole,
  f: DispatchFacts,
  attachments: { hasAgreement: boolean; hasSp3: boolean },
): string => {
  const name = fullName(f);
  const destination = [f.destinationName, f.destinationAddress].filter(Boolean).join(', ');

  // Ontario transport (MacKinnon): From/To up top — where to collect the
  // deceased and where to bring them — then the case details they require.
  if (isOntario(f) && role === 'transport') {
    return `
Hello,

Please arrange the following transfer.

Deceased Name: ${name}
Weight: ${f.deceasedWeight || 'N/A'}

From:
- ${f.placeOfDeath || 'N/A'}

To:
- ${f.destinationName || 'N/A'}
- ${f.destinationAddress || 'N/A'}

Details:
- Date of Birth: ${f.deceasedDob || 'N/A'}
- Next of Kin: ${f.nextOfKinName || 'N/A'}
- Next of Kin Phone: ${f.nextOfKinPhone || 'N/A'}
- Next of Kin Address: ${f.nextOfKinAddress || 'N/A'}

Thank you. Please confirm receipt and let us know if you need any additional information.
    `.trim();
  }

  // Ontario drop-off facility: an arrival notice with basic info only — no
  // next of kin (mirrors the QC "we will be sending you the remains" style).
  if (isOntario(f) && role === 'dropoff') {
    return `
Hello,

You should expect to receive the late ${name}, delivered by ${f.transportName || 'our transport partner'}.

Details:
- Deceased: ${name}
- Date of Birth: ${f.deceasedDob || 'N/A'}
- Weight: ${f.deceasedWeight || 'N/A'}

Thank you. Please confirm receipt and let us know if you need any additional information.
    `.trim();
  }

  if (role === 'transport') {
    return `
Hello,

Please arrange the pickup and transfer of ${name}.

Details:
- Name: ${name}
- SP3 Number: ${f.sp3Number || 'N/A'}
- Pickup: ${f.placeOfDeath || 'N/A'}
- Drop-off: ${destination || 'N/A'}

${attachments.hasSp3 ? 'The SP3 is attached.' : 'Note: No SP3 is available to attach.'}

Thank you. Please confirm receipt and let us know if you need any additional information.
    `.trim();
  }

  if (role === 'dropoff') {
    return `
Hello,

${f.transportName || 'Our transport partner'} will be delivering the remains of ${name} to your facility for storage and preparation.

Details:
- Name: ${name}
- SP3 Number: ${f.sp3Number || 'N/A'}
- Transport Company: ${f.transportName || 'N/A'}
- Crematorium: ${f.crematoriumName || 'N/A'}

${attachments.hasSp3 ? 'The SP3 is attached.' : 'Note: No SP3 is available to attach.'}

Thank you. Please confirm receipt and let us know if you need any additional information.
    `.trim();
  }

  const missingNote = isOntario(f)
    ? 'Note: No cremation agreement is available to attach.'
    : 'Note: No cremation agreement or SP3 is available to attach.';
  return `
Hello,

We will be sending you the remains of ${name} for cremation.

Details:
- Name: ${name}${isOntario(f) ? '' : `\n- SP3 Number: ${f.sp3Number || 'N/A'}`}
- Transport Company: ${f.transportName || 'N/A'}

${attachments.hasAgreement ? 'The cremation agreement is attached.' : ''}
${attachments.hasSp3 ? 'The SP3 is attached.' : ''}
${!attachments.hasAgreement && !attachments.hasSp3 ? missingNote : ''}

Thank you for your assistance. Please let us know if you need any additional information.
  `.trim();
};

// ─── Partner documents follow-up (CREMATORIUM_DOCS_FOLLOWUP_PLAN §5) ─────────

export interface PartnerDocsEmailFacts {
  funeralId: string | number;
  deceasedName: string;
  deceasedDob: string;
  /** "Sep 17, 2026" / "17 sept. 2026"; empty when unknown. */
  dispatchDate: string;
  sp3Number: string;
  transportName: string;
  isQuebec: boolean;
}

export const buildPartnerDocsSubject = (f: PartnerDocsEmailFacts): string =>
  `Cleo Cremation - Documents - ${f.deceasedName} (${f.funeralId})`;

/**
 * Default subject for a free-form email to a partner from the case feed:
 * "Ref: Edith Csaki 854". Only a starting point, so staff can edit it; it
 * gives the partner the name and case number to file the email against.
 * Whitespace in stored names is collapsed ("Bruce  Greenberg" → "Bruce Greenberg").
 */
export const buildPartnerRefSubject = (
  deceasedName: string | null | undefined,
  funeralId: string | number
): string =>
  ['Ref:', (deceasedName || '').replace(/\s+/g, ' ').trim(), String(funeralId)]
    .filter(Boolean)
    .join(' ');

/**
 * Send mode: the documents are attached. `resend` = every selected document was
 * already sent to this partner. Request mode: ask the partner to send the
 * document to us (inbound, e.g. the MCCD from the drop-off VC). Ontario is
 * English only; Quebec defaults to English with a French toggle (Q3).
 */
export const buildPartnerDocsBody = (
  mode: 'send' | 'request',
  lang: 'en' | 'fr',
  f: PartnerDocsEmailFacts,
  docs: { label: string; count: number; resend: boolean },
): string => {
  const date = f.dispatchDate;
  if (mode === 'request') {
    return `
Hello,

Could you please send us the ${docs.label.replace(/^the /, '')} for ${f.deceasedName}${
      f.deceasedDob ? ` (date of birth ${f.deceasedDob})` : ''
    }${date ? `, who was transferred to you on ${date}` : ''}?

You can reply to this email with a scan or photo attached.

Thank you.
    `.trim();
  }

  if (lang === 'fr') {
    const plural = docs.count > 1;
    const intro = docs.resend
      ? `Tel que demandé, vous trouverez ci-joint de nouveau ${docs.label}.`
      : `Vous trouverez ci-joint ${docs.label}, qui ne ${
          plural ? 'faisaient' : 'faisait'
        } pas partie de notre envoi initial.`;
    return `
Bonjour,

Pour faire suite au transfert de ${f.deceasedName}${date ? ` chez vous le ${date}` : ' chez vous'}.

${intro}

Détails :
- Nom : ${f.deceasedName}${f.isQuebec ? `\n- Numéro SP3 : ${f.sp3Number || 'N/A'}` : ''}
- Entreprise de transport : ${f.transportName || 'N/A'}

Merci. N'hésitez pas à nous contacter si vous avez besoin de renseignements supplémentaires.
    `.trim();
  }

  const intro = docs.resend
    ? `As requested, please find attached ${docs.label} again.`
    : `Please find attached ${docs.label}, which ${
        docs.count > 1 ? 'were' : 'was'
      } not included with our original request.`;
  return `
Hello,

Following up on ${f.deceasedName}${date ? `, transferred to you on ${date}` : ''}.

${intro}

Details:
- Name: ${f.deceasedName}${f.isQuebec ? `\n- SP3 Number: ${f.sp3Number || 'N/A'}` : ''}
- Transport Company: ${f.transportName || 'N/A'}

Thank you. Please let us know if you need any additional information.
  `.trim();
};
